export const OTHER_INDUSTRY = 'Other';

export type IndustryTaxonomyItem = {
  industry: string;
  menuLabel: string;
  slug: string;
};

export const CANONICAL_INDUSTRIES: readonly IndustryTaxonomyItem[] = [
  {
    industry: 'Fundraising, VC & Capital Strategy',
    menuLabel: 'Fundraising',
    slug: 'fundraising',
  },
  {
    industry: 'Startup Strategy & Entrepreneurship',
    menuLabel: 'Entrepreneurship',
    slug: 'entrepreneurship',
  },
  {
    industry: 'Marketing & GTM Strategies',
    menuLabel: 'Go-to-Market',
    slug: 'go-to-market',
  },
  {
    industry: 'Sales & Distribution',
    menuLabel: 'Sales',
    slug: 'sales',
  },
  {
    industry: 'Negotiation Strategies',
    menuLabel: 'Negotiation',
    slug: 'negotiation',
  },
  {
    industry: 'Business Operations',
    menuLabel: 'Business Operations',
    slug: 'business-operations',
  },
  {
    industry: 'Leadership & Management',
    menuLabel: 'Leadership',
    slug: 'leadership',
  },
  {
    industry: 'Communication & Behavior Science',
    menuLabel: 'Communication',
    slug: 'communication',
  },
  {
    industry: 'Productivity & Personal Performance',
    menuLabel: 'Productivity',
    slug: 'productivity',
  },
  {
    industry: 'Health, Wellbeing & Mindset',
    menuLabel: 'Health',
    slug: 'health',
  },
  {
    industry: 'Investing & Wealth Generation',
    menuLabel: 'Investing',
    slug: 'investing',
  },
  {
    industry: 'Education, Knowledge & Learning Systems',
    menuLabel: 'Education',
    slug: 'education',
  },
];

/** Canonical display order for industries. */
export const INDUSTRY_ORDER = CANONICAL_INDUSTRIES.map((item) => item.industry);

const TAXONOMY_BY_INDUSTRY = new Map(
  CANONICAL_INDUSTRIES.map((item, index) => [item.industry, { ...item, index }]),
);

const normalizeIndustryKey = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const INDUSTRY_ALIASES: Record<string, string> = {
  'Business Planning': 'Business Operations',
  Career: 'Productivity & Personal Performance',
  Communication: 'Communication & Behavior Science',
  Copywriting: 'Marketing & GTM Strategies',
  Education: 'Education, Knowledge & Learning Systems',
  Entrepreneurship: 'Startup Strategy & Entrepreneurship',
  Fundraising: 'Fundraising, VC & Capital Strategy',
  'Go-To-Market': 'Marketing & GTM Strategies',
  'Go-to-Market': 'Marketing & GTM Strategies',
  Growth: 'Marketing & GTM Strategies',
  Health: 'Health, Wellbeing & Mindset',
  Influence: 'Communication & Behavior Science',
  Investing: 'Investing & Wealth Generation',
  Leadership: 'Leadership & Management',
  Marketing: 'Marketing & GTM Strategies',
  Negotiation: 'Negotiation Strategies',
  Operations: 'Business Operations',
  Performance: 'Productivity & Personal Performance',
  'Personal Development': 'Productivity & Personal Performance',
  'Personal Finance': 'Investing & Wealth Generation',
  Positioning: 'Marketing & GTM Strategies',
  'Product Marketing': 'Marketing & GTM Strategies',
  Productivity: 'Productivity & Personal Performance',
  'Revenue Operations': 'Sales & Distribution',
  'SaaS Growth': 'Marketing & GTM Strategies',
  Sales: 'Sales & Distribution',
  'Startup Strategy': 'Startup Strategy & Entrepreneurship',
  'Stock Trading': 'Investing & Wealth Generation',
  Strategy: 'Business Operations',
  'Wealth Building': 'Investing & Wealth Generation',
  'Wellness & Health': 'Health, Wellbeing & Mindset',
};

const CANONICAL_INDUSTRY_BY_KEY = new Map<string, string>();
for (const item of CANONICAL_INDUSTRIES) {
  CANONICAL_INDUSTRY_BY_KEY.set(normalizeIndustryKey(item.industry), item.industry);
  CANONICAL_INDUSTRY_BY_KEY.set(normalizeIndustryKey(item.menuLabel), item.industry);
}
for (const [alias, industry] of Object.entries(INDUSTRY_ALIASES)) {
  CANONICAL_INDUSTRY_BY_KEY.set(normalizeIndustryKey(alias), industry);
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export function getCanonicalIndustry(industry?: string | null): string | undefined {
  const displayName = industry?.trim().replace(/\s+/g, ' ');
  if (!displayName) return undefined;
  return CANONICAL_INDUSTRY_BY_KEY.get(normalizeIndustryKey(displayName));
}

export function getIndustryDisplayName(industry?: string | null): string {
  const displayName = industry?.trim().replace(/\s+/g, ' ');
  if (!displayName) return OTHER_INDUSTRY;
  return getCanonicalIndustry(displayName) || displayName;
}

export function getIndustryTaxonomyItem(industry?: string | null) {
  const canonicalIndustry = getCanonicalIndustry(industry);
  return canonicalIndustry ? TAXONOMY_BY_INDUSTRY.get(canonicalIndustry) : undefined;
}

export function isCanonicalIndustry(industry?: string | null): boolean {
  const displayName = industry?.trim().replace(/\s+/g, ' ');
  return Boolean(displayName && TAXONOMY_BY_INDUSTRY.has(displayName));
}

export function getIndustryMenuLabel(industry?: string | null): string {
  const displayName = getIndustryDisplayName(industry);
  return getIndustryTaxonomyItem(displayName)?.menuLabel || displayName;
}

export function getIndustrySlug(industry?: string | null): string {
  const displayName = getIndustryDisplayName(industry);
  return getIndustryTaxonomyItem(displayName)?.slug || slugify(displayName) || 'other';
}

export function getIndustryAnchorId(industry?: string | null): string {
  return `industry-${getIndustrySlug(industry)}`;
}

/**
 * Sort industry names by the canonical order.
 * Unknown industries go after the defined ones (but before "Other").
 * "Other" is always last.
 */
export function industrySort(a: string, b: string): number {
  const displayA = getIndustryDisplayName(a);
  const displayB = getIndustryDisplayName(b);

  if (displayA === displayB) return 0;
  if (displayA === OTHER_INDUSTRY && displayB === OTHER_INDUSTRY) return 0;
  if (displayA === OTHER_INDUSTRY) return 1;
  if (displayB === OTHER_INDUSTRY) return -1;

  const taxonomyA = getIndustryTaxonomyItem(displayA);
  const taxonomyB = getIndustryTaxonomyItem(displayB);

  if (!taxonomyA && !taxonomyB) return displayA.localeCompare(displayB);
  if (!taxonomyA) return 1;
  if (!taxonomyB) return -1;
  return taxonomyA.index - taxonomyB.index;
}

export function groupByIndustry<T extends { industry?: string | null }>(
  items: readonly T[],
): Array<[string, T[]]> {
  const groups: Record<string, T[]> = {};

  for (const item of items) {
    const industry = getIndustryDisplayName(item.industry);
    if (!groups[industry]) groups[industry] = [];
    groups[industry].push(item);
  }

  return Object.entries(groups).sort(([a], [b]) => industrySort(a, b));
}
